<template>
  <!--
    A lane that does not exist renders as "not applicable", never as a problem. Listing-only sources
    (canada_buys, nunavut) have no scrape lane at all because their scrape() throws and no scrape job
    is ever enqueued — the absence is by design, and colouring it red would be a permanent false
    alarm on two sources.
  -->
  <span
    v-if="!lane"
    class="scraper-health-lane-cell__na"
    v-tooltip="$t('page.admin-scraper-health.lane.not-applicable')"
  >
    —
  </span>

  <!--
    A dot and a word, not a tag. These three columns are the EVIDENCE behind the Service column, and
    rendering them as tags gave four identically-weighted pills per row — the one that answers the
    question was indistinguishable from the three that explain it. Demoting them leaves Service as
    the only pill on the row, and takes ~13px off every row height into the bargain.
  -->
  <div v-else class="scraper-health-lane-cell">
    <span class="scraper-health-lane-cell__state">
      <span class="scraper-health-lane-cell__dot" :class="`scraper-health-lane-cell__dot--${tone}`"></span>
      {{ stateLabel }}
    </span>
    <!-- The tooltip carries the absolute date. Repeating the relative text, as this used to, made
         hovering pointless — the one thing you cannot get from "181 d 23 h ago" is the date. -->
    <span v-if="detail" class="scraper-health-lane-cell__detail" v-tooltip="detailTitle">{{ detail }}</span>
  </div>
</template>

<script>
import { getScraperHealthStateTone } from '@/util/tag-tones/scraper-health-state-tag';
import { formatAbsolute, formatRelativeToNow, isLaneProblem } from '@/util/scraper-health-util';

export default {
  name: 'ScraperHealthLaneCell',
  props: {
    /** Null when the source has no such lane — see the template comment. */
    lane: {
      type: Object,
      default: null,
    },
  },
  computed: {
    tone() {
      return getScraperHealthStateTone(this.lane.state);
    },
    stateLabel() {
      return this.$t(`page.admin-scraper-health.states.${this.lane.state}`);
    },
    /**
     * The absolute date behind whichever timestamp `detail` chose, so the tooltip adds information
     * instead of echoing it. Empty for the paused case, whose detail is a state name, not a time.
     */
    detailTitle() {
      if (this.lane.state === 'paused') return '';
      const value = isLaneProblem(this.lane) ? this.lane.problemSince || this.lane.since : this.lane.lastSuccessAt;
      return formatAbsolute(value);
    },
    /**
     * How long it has been wrong, for a problem; how long since it last produced anything, for a
     * healthy lane. Both answer "should I be worried", which a bare state badge cannot.
     */
    detail() {
      if (isLaneProblem(this.lane)) {
        const since = this.lane.problemSince || this.lane.since;
        return formatRelativeToNow(since, this.$i18n.locale);
      }
      if (this.lane.state === 'paused') {
        // Show what it would be if un-paused: that is the question a paused row raises.
        return this.$t('page.admin-scraper-health.lane.paused-underlying', {
          state: this.$t(`page.admin-scraper-health.states.${this.lane.computedState}`),
        });
      }
      return this.lane.lastSuccessAt ? formatRelativeToNow(this.lane.lastSuccessAt, this.$i18n.locale) : '';
    },
  },
};
</script>

<style lang="scss">
.scraper-health-lane-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;

  &__state {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: $color-normal;
  }

  // Colour carries the state, the word confirms it — neither alone. Colour-blind readers get the
  // label; a quick scan gets the colour.
  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: none;
    background: $color-slate-400;

    &--success {
      background: $color-semantic-success;
    }

    &--warning {
      background: $color-semantic-warning;
    }

    &--destructive {
      background: $color-semantic-error;
    }

    &--orange {
      background: $color-orange;
    }

    &--neutral {
      background: $color-slate-400;
    }
  }

  &__detail {
    font-size: 12px;
    color: $color-text-muted;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 150px;
  }

  &__na {
    color: $color-text-muted;
  }
}
</style>
